import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Filter, ExternalLink } from "lucide-react";
import { BOOKMAKERS } from "../lib/data";
import { BrandBadge } from "../components/BrandBadge";
import { StarRating } from "../components/StarRating";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SEO, breadcrumbSchema } from "../components/SEO";

const BONUS_TYPES = [
    { key: "all", label: "Tous" },
    { key: "sans-depot", label: "Sans dépôt" },
    { key: "avec-depot", label: "Avec dépôt" },
    { key: "freebet", label: "Freebet" },
];



const Comparatif = () => {
    const [bonusType, setBonusType] = useState("all");
    const [expanded, setExpanded] = useState(null);

    const filtered = useMemo(() => {
        return BOOKMAKERS.filter((b) => {
            if (bonusType === "sans-depot") {
                return b.tags.some(tag => tag.toLowerCase().includes("sans dépôt"));
            }
            if (bonusType === "freebet") {
                return b.bonus.toLowerCase().includes("freebet") || b.tags.some(tag => tag.toLowerCase().includes("freebet"));
            }
            if (bonusType === "avec-depot") {
                return !b.tags.some(tag => tag.toLowerCase().includes("sans dépôt"));
            }
            return true;
        });
    }, [bonusType]);

    return (
        <div data-testid="comparatif-page">
            <SEO
                title="Comparatif bookmakers paris foot 2026 | Pronospot.com"
                description="Comparez en un coup d'œil les 15 meilleurs bookmakers agréés ANJ pour parier sur le football : bonus, cotes, marchés, app mobile et plus."
                jsonLd={breadcrumbSchema([
                    { name: "Accueil", url: "https://pronospot.com/" },
                    {
                        name: "Comparatif",
                        url: "https://pronospot.com/comparatif",
                    },
                ])}
            />
            <section className="container-x pt-10 pb-6">
                <Breadcrumbs items={[{ label: "Comparatif bookmakers" }]} />
                <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white tracking-tighter">
                    Comparatif 2026 : Quel est le meilleur bookmaker pour le foot ?
                </h1>
                <p className="text-gray-400 mt-3 max-w-3xl">
                    Comparez en un coup d'œil les 15 meilleurs bookmakers
                    agréés ANJ pour parier sur le football en 2026. Filtrez
                    par type de bonus.
                </p>
            </section>

            {/* Filters */}
            <section className="container-x pb-8" data-testid="filters-section">
                <div className="card-elev p-5 sm:p-6">
                    <div className="flex items-center gap-2 mb-5">
                        <Filter size={16} className="text-[#00C853]" />
                        <span className="font-heading font-semibold text-white text-sm uppercase tracking-wider">
                            Filtres
                        </span>
                    </div>

                    <div>
                        <label className="text-xs text-gray-400 mb-2 block uppercase tracking-wider">
                            Type de bonus
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {BONUS_TYPES.map((b) => (
                                <button
                                    key={b.key}
                                    onClick={() => setBonusType(b.key)}
                                    data-testid={`filter-bonus-${b.key}`}
                                    className={`text-sm px-4 py-2 rounded-full border transition-all ${
                                        bonusType === b.key
                                            ? "bg-[#00C853] text-black border-[#00C853] font-semibold"
                                            : "bg-transparent text-slate-600 dark:text-gray-300 border-slate-300 dark:border-[#2A4A6B] hover:border-[#00C853]"
                                    }`}
                                >
                                    {b.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Table */}
            <section className="container-x pb-20" data-testid="comparison-table-section">
                <div className="card-elev p-0 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm" data-testid="comparison-table">
                            <thead className="bg-[#1E3A5F]">
                                <tr>
                                    <th className="px-5 py-4 font-heading font-semibold text-white uppercase text-xs tracking-wider">
                                        Bookmaker
                                    </th>
                                    <th className="px-5 py-4 font-heading font-semibold text-white uppercase text-xs tracking-wider">
                                        Bonus
                                    </th>
                                    <th className="px-5 py-4 font-heading font-semibold text-white uppercase text-xs tracking-wider hidden md:table-cell">
                                        Cotes moy.
                                    </th>
                                    <th className="px-5 py-4 font-heading font-semibold text-white uppercase text-xs tracking-wider hidden lg:table-cell">
                                        Marchés
                                    </th>
                                    <th className="px-5 py-4 font-heading font-semibold text-white uppercase text-xs tracking-wider hidden lg:table-cell">
                                        Live
                                    </th>
                                    <th className="px-5 py-4 font-heading font-semibold text-white uppercase text-xs tracking-wider">
                                        Note
                                    </th>
                                    <th className="px-5 py-4 font-heading font-semibold text-white uppercase text-xs tracking-wider"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((b, idx) => (
                                    <React.Fragment key={b.slug}>
                                        <tr
                                            data-testid={`compare-row-${b.slug}`}
                                            onClick={() =>
                                                setExpanded(
                                                    expanded === b.slug
                                                        ? null
                                                        : b.slug,
                                                )
                                            }
                                            className={`border-t border-[#2A4A6B] cursor-pointer transition-colors ${
                                                idx % 2 === 0
                                                    ? "bg-[#0D1B2A]"
                                                    : "bg-[#162236]"
                                            } hover:bg-[#1E3A5F]/60`}
                                        >
                                            <td className="px-5 py-5">
                                                <Link
                                                    to={`/avis/${b.slug}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="flex items-center gap-3 flex-wrap hover:opacity-80 transition-opacity"
                                                >
                                                    <BrandBadge
                                                        name={b.name}
                                                        color={b.color}
                                                        size="sm"
                                                    />
                                                </Link>
                                            </td>
                                            <td className="px-5 py-5 text-[#FFB300] font-semibold">
                                                {b.bonus}
                                            </td>
                                            <td className="px-5 py-5 text-gray-300 hidden md:table-cell">
                                                {b.oddsAvg}
                                            </td>
                                            <td className="px-5 py-5 text-gray-300 hidden lg:table-cell">
                                                {b.markets}+
                                            </td>
                                            <td className="px-5 py-5 hidden lg:table-cell">
                                                {b.liveStreaming ? (
                                                    <span className="chip">
                                                        Oui
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-500 text-xs">
                                                        Non
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-5 py-5">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-heading font-bold text-white text-base">
                                                        {b.score.toFixed(1)}
                                                    </span>
                                                    <StarRating
                                                        value={b.stars}
                                                        size={12}
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-5 py-5">
                                                <a
                                                    href={b.affiliateUrl}
                                                    target="_blank"
                                                    rel="sponsored nofollow noopener"
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                    className="btn-green !py-2 !px-4 text-xs whitespace-nowrap"
                                                    data-testid={`cta-table-${b.slug}`}
                                                >
                                                    Voir le bookmaker
                                                    <ExternalLink size={12} />
                                                </a>
                                            </td>
                                        </tr>
                                        {expanded === b.slug && (
                                            <tr
                                                className="bg-[#0A1623]"
                                                data-testid={`expanded-${b.slug}`}
                                            >
                                                <td
                                                    colSpan={7}
                                                    className="px-5 py-6"
                                                >
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                        <div>
                                                            <h4 className="font-heading font-bold text-[#00C853] text-sm mb-3 uppercase tracking-wider">
                                                                Points forts
                                                            </h4>
                                                            <ul className="space-y-2">
                                                                {b.pros
                                                                    .slice(0, 3)
                                                                    .map(
                                                                        (p) => (
                                                                            <li
                                                                                key={
                                                                                    p
                                                                                }
                                                                                className="text-sm text-gray-300 flex gap-2"
                                                                            >
                                                                                <span className="text-[#00C853]">
                                                                                    ✓
                                                                                </span>{" "}
                                                                                {
                                                                                    p
                                                                                }
                                                                            </li>
                                                                        ),
                                                                    )}
                                                            </ul>
                                                        </div>
                                                        <div>
                                                            <h4 className="font-heading font-bold text-[#FFB300] text-sm mb-3 uppercase tracking-wider">
                                                                Points faibles
                                                            </h4>
                                                            <ul className="space-y-2">
                                                                {b.cons.map(
                                                                    (c) => (
                                                                        <li
                                                                            key={
                                                                                c
                                                                            }
                                                                            className="text-sm text-gray-300 flex gap-2"
                                                                        >
                                                                            <span className="text-[#FFB300]">
                                                                                ✗
                                                                            </span>{" "}
                                                                            {c}
                                                                        </li>
                                                                    ),
                                                                )}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                    <div className="mt-5">
                                                        <Link
                                                            to={`/avis/${b.slug}`}
                                                            className="text-[#00C853] text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
                                                        >
                                                            Lire l'avis complet
                                                            <ChevronRight
                                                                size={14}
                                                            />
                                                        </Link>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <p className="text-xs text-gray-500 mt-5">
                    Cliquez sur une ligne pour afficher les détails. 18+ |
                    Jouer comporte des risques.
                </p>
            </section>
        </div>
    );
};

export default Comparatif;
